import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";

import {
  DEFAULT_LANGUAGE,
  getLanguage,
  isLanguageCode,
  type LanguageCode,
} from "./languages";

/**
 * Active-language context — the single source of truth for which
 * language the learner is studying right now.
 *
 * Persisted in localStorage so the choice survives refresh /
 * bookmark / new-tab. Default is German (the visa track).
 *
 * Phase 1F intentionally avoids URL-segment routing — see
 * `docs/multilingual-plan.md` for the rationale. Promote to URL
 * later if shareability becomes a real requirement.
 */

const STORAGE_KEY = "lingua.activeLanguage";

type ActiveLanguageValue = {
  language: LanguageCode;
  setLanguage: (code: LanguageCode) => void;
};

const ActiveLanguageContext = createContext<ActiveLanguageValue | null>(null);

/** Read the stored value once, synchronously, before first paint.
 *  Falls back to the default for empty / corrupt / SSR values.
 *
 *  Exported so non-React code paths (e.g. TanStack Router `beforeLoad`)
 *  can pick up the active language without going through the context. */
export function readStoredLanguage(): LanguageCode {
  if (typeof window === "undefined") return DEFAULT_LANGUAGE;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (raw && isLanguageCode(raw)) return raw;
  } catch {
    // SecurityError in restricted contexts (Safari private mode etc.) —
    // fall through to default.
  }
  return DEFAULT_LANGUAGE;
}

export function ActiveLanguageProvider({ children }: { children: ReactNode }) {
  // Read synchronously on first render so we never flash the default
  // German content for users whose stored choice is non-German.
  const [language, setLanguageState] = useState<LanguageCode>(() =>
    readStoredLanguage(),
  );

  const setLanguage = useCallback((next: LanguageCode) => {
    setLanguageState(next);
    try {
      window.localStorage.setItem(STORAGE_KEY, next);
    } catch {
      // Same SecurityError swallow as above — the in-memory state still
      // works for the session, just won't survive refresh.
    }
  }, []);

  // Keep `document.title` in sync. S7 in the plan; landing it here is
  // cheap and keeps the provider as the only place that knows about
  // the active language across the whole app.
  useEffect(() => {
    if (typeof document !== "undefined") {
      document.title = `lingua · ${language}`;
    }
  }, [language]);

  const value = useMemo(
    () => ({ language, setLanguage }),
    [language, setLanguage],
  );

  return (
    <ActiveLanguageContext.Provider value={value}>
      {children}
    </ActiveLanguageContext.Provider>
  );
}

export function useActiveLanguage(): ActiveLanguageValue {
  const ctx = useContext(ActiveLanguageContext);
  if (!ctx) {
    throw new Error(
      "useActiveLanguage must be called inside <ActiveLanguageProvider>",
    );
  }
  return ctx;
}

/** Convenience: just the code, no setter. Use when you only need to
 *  read it for a query parameter or a label. */
export function useLanguageCode(): LanguageCode {
  return useActiveLanguage().language;
}

/** Returns true when the active language has no seeded content yet.
 *  Routes use this to decide whether to render their normal content or
 *  fall back to `<UnseededLanguageLanding>`. */
export function useActiveLanguageIsUnseeded(): boolean {
  const meta = getLanguage(useLanguageCode());
  return meta ? !meta.available : false;
}
